import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';

import { PostHogInstance } from 'shared/posthogCapture/types';
import { EmailProvider } from 'shared/components/EmailComposeMenu/_index';
import { EventStatus } from 'events/_types';

interface EventEmailSentProperties {
  provider: EmailProvider;
  event_id: string;
}

interface EventViewedProperties {
  event_id: string;
  note_count: number;
}

interface EventVersionHistoryViewedProperties {
  event_id: string;
  note_count: number;
}

interface EventVersionHistoryEmailSentProperties {
  provider: EmailProvider;
  event_id: string;
}

interface EventVersionHistoryCopiedProperties {
  event_id: string;
}

interface EventPinProperties {
  event_id: string;
}

interface EventsHomeDateSelectedProperties {
  date: string;
}

interface EventOpenedProperties {
  event_id: string;
  status: EventStatus;
}

interface EventsHomeEventSelectedProperties {
  event_id: string;
}

interface DaySummaryDepartmentChangedProperties {
  department_id: string;
}

interface DaySummaryEditPreferencesOptionProperties {
  preference: string;
}

interface DaySummaryEditPreferencesSavedProperties {
  count: number;
}

type FilterCategory =
  | 'urgency'
  | 'department'
  | 'people'
  | 'tags'
  | 'visibility'
  | 'source'
  | 'signal';

type GroupingValue = 'default' | 'urgency' | 'status' | 'visibility';

interface FilterCategoryProperties {
  category: FilterCategory;
}

interface FilterValueSelectedProperties {
  category: FilterCategory;
  value: string;
}

interface GroupingChangedProperties {
  grouping: GroupingValue;
}

export const createEventsCaptureEvents = (posthog: PostHogInstance) => ({
  eventViewed: (properties: EventViewedProperties) => {
    posthog?.capture('events / event / viewed', properties);
  },
  eventEmailSent: (properties: EventEmailSentProperties) => {
    posthog?.capture('events / event / email sent', properties);
  },
  eventPinned: (properties: EventPinProperties) => {
    posthog?.capture('events / event / pin / selected', properties);
  },
  eventUnpinned: (properties: EventPinProperties) => {
    posthog?.capture('events / event / pin / deselected', properties);
  },
  eventBackClicked: () => {
    posthog?.capture('events / event / back clicked');
  },
  eventVersionHistoryViewed: (
    properties: EventVersionHistoryViewedProperties
  ) => {
    posthog?.capture('events / event / version history / viewed', properties);
  },
  eventVersionHistoryEmailSent: (
    properties: EventVersionHistoryEmailSentProperties
  ) => {
    posthog?.capture(
      'events / event / version history / email sent',
      properties
    );
  },
  eventVersionHistoryCopied: (
    properties: EventVersionHistoryCopiedProperties
  ) => {
    posthog?.capture('events / event / version history / copied', properties);
  },
  eventsHomeSearchClicked: () => {
    posthog?.capture('events / home / search clicked');
  },
  eventsHomeCalendarClicked: () => {
    posthog?.capture('events / home / calendar clicked');
  },
  weekWidgetDaySelected: (properties: { days_from_today: number }) => {
    posthog?.capture('events / week widget / day / selected', properties);
  },
  weekWidgetWeekChanged: (properties: { direction: 'previous' | 'next' }) => {
    posthog?.capture('events / week widget / week / changed', properties);
  },
  eventsHomeDateSelected: (properties: EventsHomeDateSelectedProperties) => {
    posthog?.capture('events / home / date selected', properties);
  },
  eventsHomeEventSelected: (properties: EventsHomeEventSelectedProperties) => {
    posthog?.capture('events / home / event selected', properties);
  },
  eventOpened: (properties: EventOpenedProperties) => {
    posthog?.capture('events / event / opened', properties);
  },
  daySummaryCollapsed: () => {
    posthog?.capture('events / day summary / collapsed');
  },
  daySummaryDepartmentChanged: (
    properties: DaySummaryDepartmentChangedProperties
  ) => {
    posthog?.capture('events / day summary / department / changed', properties);
  },
  daySummaryExpanded: () => {
    posthog?.capture('events / day summary / expanded');
  },
  daySummaryMenuEditPreferencesClicked: () => {
    posthog?.capture(
      'events / day summary / menu / edit preferences / clicked'
    );
  },
  daySummaryMenuFeedbackClicked: () => {
    posthog?.capture('events / day summary / menu / feedback / clicked');
  },
  daySummaryMenuShareClicked: () => {
    posthog?.capture('events / day summary / menu / share / clicked');
  },
  daySummaryEmailSent: (properties: { provider: EmailProvider }) => {
    posthog?.capture('events / day summary / email sent', properties);
  },
  daySummaryEditPreferencesOptionSelected: (
    properties: DaySummaryEditPreferencesOptionProperties
  ) => {
    posthog?.capture(
      'events / day summary / edit preferences / option / selected',
      properties
    );
  },
  daySummaryEditPreferencesOptionDeselected: (
    properties: DaySummaryEditPreferencesOptionProperties
  ) => {
    posthog?.capture(
      'events / day summary / edit preferences / option / deselected',
      properties
    );
  },
  daySummaryEditPreferencesSaved: (
    properties: DaySummaryEditPreferencesSavedProperties
  ) => {
    posthog?.capture(
      'events / day summary / edit preferences / saved',
      properties
    );
  },
  daySummaryEditPreferencesCancelled: () => {
    posthog?.capture('events / day summary / edit preferences / cancelled');
  },
  filterCategoryOpened: (properties: FilterCategoryProperties) => {
    posthog?.capture('events / filter / category / opened', properties);
  },
  filterOpened: () => {
    posthog?.capture('events / filter / opened');
  },
  filterReset: (properties?: { from: 'menu' | 'empty_state' }) => {
    posthog?.capture('events / filter / reset', properties);
  },
  filterValueCleared: (properties: FilterCategoryProperties) => {
    posthog?.capture('events / filter / value / cleared', properties);
  },
  filterValueSelected: (properties: FilterValueSelectedProperties) => {
    posthog?.capture('events / filter / value / selected', properties);
  },
  groupingChanged: (properties: GroupingChangedProperties) => {
    posthog?.capture('events / grouping / changed', properties);
  },
  groupingOpened: () => {
    posthog?.capture('events / grouping / opened');
  },
});

export type EventsCaptureEvents = ReturnType<typeof createEventsCaptureEvents>;

export const usePosthogCapture = () => {
  const posthog = usePostHog();
  return useMemo(() => createEventsCaptureEvents(posthog), [posthog]);
};
